import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Eye, Users, CalendarDays, TrendingUp } from "lucide-react";

const PAGE_LABELS = {
  "/": "Beranda",
  "/profil": "Profil BPBD",
  "/bidang": "Bidang PB",
  "/informasi": "Info Kebencanaan",
  "/informasi-publik": "Informasi Publik",
  "/layanan": "Layanan",
  "/berita": "Berita",
  "/kegiatan": "Kegiatan",
  "/produk-hukum": "Produk Hukum",
  "/faq": "FAQ",
  "/galeri": "Galeri",
  "/tautan": "Tautan Terkait",
};

const pageLabel = (path) => {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith("/berita/")) return "Detail Berita";
  return path;
};

const fmtDay = (day) => new Date(`${day}T00:00:00`).toLocaleDateString("id-ID", { day: "numeric", month: "short" });

export const VisitStats = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats/visits").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-400 py-10 text-center" data-testid="stats-loading">Memuat statistik...</div>;

  const cards = [
    { label: "Total Kunjungan", value: stats.total_views, icon: Eye, color: "text-orange-600 bg-orange-50" },
    { label: "Pengunjung Unik", value: stats.unique_visitors, icon: Users, color: "text-sky-600 bg-sky-50" },
    { label: "Kunjungan Hari Ini", value: stats.views_today, icon: CalendarDays, color: "text-emerald-600 bg-emerald-50" },
    { label: "Pengunjung Hari Ini", value: stats.unique_today, icon: TrendingUp, color: "text-violet-600 bg-violet-50" },
  ];

  const chartData = stats.daily.map((d) => ({ ...d, label: fmtDay(d.day) }));
  const maxViews = Math.max(1, ...stats.top_pages.map((p) => p.views));

  return (
    <div data-testid="admin-visit-stats" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5 hover-lift" data-testid={`stat-card-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-3xl font-bold text-slate-900">{c.value.toLocaleString("id-ID")}</p>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.color}`}><c.icon size={18} /></span>
            </div>
            <p className="section-label mt-2 text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="stats-daily-chart">
        <h3 className="font-heading font-bold text-slate-900 mb-5">Kunjungan 14 Hari Terakhir</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" fontSize={11} fontFamily="JetBrains Mono" />
              <YAxis fontSize={11} fontFamily="JetBrains Mono" allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="views" name="Kunjungan" fill="#EA580C" radius={[3, 3, 0, 0]} />
              <Bar dataKey="visitors" name="Pengunjung Unik" fill="#0284C7" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="stats-top-pages">
        <h3 className="font-heading font-bold text-slate-900 mb-5">Halaman Terpopuler</h3>
        {stats.top_pages.length === 0 ? (
          <p className="text-slate-400 text-center py-6">Belum ada data kunjungan.</p>
        ) : (
          <div className="space-y-3">
            {stats.top_pages.map((p, i) => (
              <div key={p.path} className="flex items-center gap-4" data-testid={`top-page-${i}`}>
                <span className="font-mono text-xs text-slate-400 w-5 text-right">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm text-slate-900 truncate">{pageLabel(p.path)}</p>
                    <p className="font-mono text-xs text-slate-500 shrink-0 ml-3">{p.views.toLocaleString("id-ID")}x · {p.visitors.toLocaleString("id-ID")} pengunjung</p>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-600 rounded-full transition-all" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                  </div>
                  <p className="font-mono text-[10px] text-slate-400 mt-0.5">{p.path}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
